import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import {
  Download,
  Eye,
  EyeOff,
  File,
  FileSpreadsheet,
  FileText,
  Image as ImageIcon,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { fetchMailAttachmentBlob, type MailAttachment } from '@/api/mail';

const PdfPreview = lazy(() => import('./PdfPreview').then((m) => ({ default: m.PdfPreview })));
const SpreadsheetPreview = lazy(() =>
  import('./SpreadsheetPreview').then((m) => ({ default: m.SpreadsheetPreview }))
);
const DocxPreview = lazy(() => import('./DocxPreview').then((m) => ({ default: m.DocxPreview })));

export const SPREADSHEET_MIME_TYPES = new Set([
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel.sheet.macroenabled.12',
  'application/vnd.oasis.opendocument.spreadsheet',
  'text/csv',
  'text/tab-separated-values',
]);

export const DOCX_MIME_TYPE =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

type PreviewKind = 'pdf' | 'sheet' | 'docx' | 'image' | null;

function previewKind(mime: string): PreviewKind {
  if (mime === 'application/pdf') return 'pdf';
  if (SPREADSHEET_MIME_TYPES.has(mime)) return 'sheet';
  if (mime === DOCX_MIME_TYPE) return 'docx';
  if (mime.startsWith('image/')) return 'image';
  return null;
}

function AttachmentIcon({ mime }: { mime: string }) {
  const kind = previewKind(mime);
  if (kind === 'image') return <ImageIcon className="h-4 w-4 shrink-0 text-muted-foreground" />;
  if (kind === 'sheet') return <FileSpreadsheet className="h-4 w-4 shrink-0 text-muted-foreground" />;
  if (kind === 'pdf' || kind === 'docx')
    return <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />;
  return <File className="h-4 w-4 shrink-0 text-muted-foreground" />;
}

function formatSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Snapshot attachments. The blob route needs the bearer header, so nothing
 * here points an <img>/<a href> straight at it: bytes are fetched once through
 * the API client and held as an object URL, which serves both the preview and
 * the download.
 */
function AttachmentRow({
  messageId,
  attachment,
}: {
  messageId: string;
  attachment: MailAttachment;
}) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const urlRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, []);

  const ensureBlob = useCallback(async (): Promise<string | null> => {
    if (urlRef.current) return urlRef.current;
    setLoading(true);
    setError(null);
    try {
      const blob = await fetchMailAttachmentBlob(messageId, attachment.id);
      const url = URL.createObjectURL(blob);
      urlRef.current = url;
      setObjectUrl(url);
      return url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load attachment');
      return null;
    } finally {
      setLoading(false);
    }
  }, [messageId, attachment.id]);

  const kind = previewKind(attachment.mime);

  const handleToggle = async () => {
    if (expanded) {
      setExpanded(false);
      return;
    }
    const url = await ensureBlob();
    if (url) setExpanded(true);
  };

  const handleDownload = async () => {
    const url = await ensureBlob();
    if (!url) return;
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = attachment.filename || 'attachment';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  };

  return (
    <div className="rounded-lg border border-border/60">
      <div className="flex items-center gap-2 px-3 py-2">
        <AttachmentIcon mime={attachment.mime} />
        <span className="min-w-0 flex-1 truncate text-sm" title={attachment.filename}>
          {attachment.filename || 'Attachment'}
        </span>
        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
          {formatSize(attachment.size)}
        </span>
        {kind && (
          <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs" onClick={handleToggle}>
            {loading ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : expanded ? (
              <EyeOff className="h-3 w-3" />
            ) : (
              <Eye className="h-3 w-3" />
            )}
            {expanded ? 'Hide' : 'Preview'}
          </Button>
        )}
        <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs" onClick={handleDownload}>
          <Download className="h-3 w-3" />
          Save
        </Button>
      </div>

      {error && <div className="px-3 pb-2 text-xs text-danger-ink">{error}</div>}

      {expanded && objectUrl && (
        <div className="border-t border-border/60 p-2">
          <Suspense
            fallback={
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            }
          >
            {kind === 'pdf' && <PdfPreview file={objectUrl} filename={attachment.filename} />}
            {kind === 'sheet' && (
              <SpreadsheetPreview url={objectUrl} filename={attachment.filename} />
            )}
            {kind === 'docx' && <DocxPreview url={objectUrl} filename={attachment.filename} />}
            {kind === 'image' && (
              <img
                src={objectUrl}
                alt={attachment.filename}
                className="mx-auto max-h-[70vh] w-auto max-w-full rounded"
              />
            )}
          </Suspense>
        </div>
      )}
    </div>
  );
}

export function AttachmentList({
  messageId,
  attachments,
  className,
}: {
  messageId: string;
  attachments: MailAttachment[];
  className?: string;
}) {
  // Inline images are already rendered in the body; listing them again as
  // files is noise. A snapshot with only inline parts shows no list at all.
  const visible = attachments.filter((a) => !a.inline);
  if (visible.length === 0) {
    return null;
  }
  return (
    <div className={cn('space-y-2', className)}>
      <div className="text-xs font-medium text-muted-foreground">
        {visible.length} attachment{visible.length === 1 ? '' : 's'}
      </div>
      {visible.map((attachment) => (
        <AttachmentRow key={attachment.id} messageId={messageId} attachment={attachment} />
      ))}
    </div>
  );
}
